import React, { useState, useRef, useCallback, useEffect } from "react";
import { Stage, Layer, Image, Circle, Text } from "react-konva";
import Konva from "konva";
import "./App.css";
import { Button, Paper, Typography, Box, IconButton } from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Delete";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ExportModal from "./components/ExportModal";
import WelcomeScreen from "./components/WelcomeScreen";
import Toast from "./components/Toast";
import ColorHarmonyPanel from "./components/ColorHarmonyPanel";
import { FileWithPath, useDropzone } from "react-dropzone";
import { ColorSpot, extractDominantColors, rgbToHex } from "./utils/colorUtils";
import {
  exportWithBackground,
  exportTransparent,
  exportWhiteBackground,
  exportColorSwatches,
} from "./utils/exportUtils";
import { useCanvasInteraction } from "./hooks/useCanvasInteraction";

function App() {
  const [image, setImage] = useState<HTMLImageElement | null>(null);
  const [colorSpots, setColorSpots] = useState<ColorSpot[]>([]);
  const [stageTransform, setStageTransform] = useState({ scale: 1, x: 0, y: 0 });
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
  const stageRef = useRef<Konva.Stage>(null);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [nextId, setNextId] = useState(1);
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'info' | 'error' } | null>(null);
  const [harmonyColorId, setHarmonyColorId] = useState<number | null>(null);

  const {
    isSpacePressed,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleWheel,
    getCursor,
  } = useCanvasInteraction({
    image,
    stageRef,
    stageTransform,
    setStageTransform,
  });

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({ width: window.innerWidth, height: window.innerHeight });
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleImageUpload = useCallback((acceptedFiles: FileWithPath[]) => {
    const file = acceptedFiles[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/bmp'];
    if (!allowedTypes.includes(file.type)) {
      alert('Please select a valid image file (JPEG, PNG, GIF, WebP, or BMP)');
      return;
    }

    // Validate file size (max 20MB)
    const maxSize = 20 * 1024 * 1024;
    if (file.size > maxSize) {
      alert('File size exceeds 20MB limit');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new window.Image();
      img.onload = () => {
        const { width, height } = img;
        const scale = Math.min(
          (window.innerWidth * 0.8) / width,
          (window.innerHeight * 0.8) / height
        );
        const stageWidth =
          window.innerWidth > 768
            ? window.innerWidth * 0.65
            : window.innerWidth * 0.95;
        const scaledImageWidth = width * scale;
        const centerX = (stageWidth - scaledImageWidth) / 2;

        setImage(img);
        setImageSize({ width, height });
        setColorSpots([]);
        setStageTransform({ scale, x: centerX, y: 0 });
        setNextId(1);
      };
      img.src = e.target?.result as string;
    };
    reader.readAsDataURL(file);
  }, []);

  const handleImageUrlLoad = useCallback((url: string): Promise<void> => {
    const loadImage = (imageUrl: string): Promise<HTMLImageElement> => {
      return new Promise((resolve, reject) => {
        const img = new window.Image();
        img.crossOrigin = "anonymous";
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error("Failed to load"));
        img.src = imageUrl;
      });
    };

    const setImageState = (img: HTMLImageElement) => {
      const { width, height } = img;
      const scale = Math.min(
        (window.innerWidth * 0.8) / width,
        (window.innerHeight * 0.8) / height
      );
      const stageWidth =
        window.innerWidth > 768
          ? window.innerWidth * 0.65
          : window.innerWidth * 0.95;
      const scaledImageWidth = width * scale;
      const centerX = (stageWidth - scaledImageWidth) / 2;

      setImage(img);
      setImageSize({ width, height });
      setColorSpots([]);
      setStageTransform({ scale, x: centerX, y: 0 });
      setNextId(1);
    };

    return new Promise(async (resolve, reject) => {
      // Try loading directly first (works for images with CORS headers)
      try {
        const img = await loadImage(url);
        setImageState(img);
        resolve();
        return;
      } catch {
        // Direct load failed, try CORS proxy
      }

      // Try with CORS proxy
      const proxyUrl = `https://corsproxy.io/?${encodeURIComponent(url)}`;
      try {
        const img = await loadImage(proxyUrl);
        setImageState(img);
        resolve();
      } catch {
        reject(new Error("Failed to load image. Please check the URL or try a different image."));
      }
    });
  }, []);

  const onDrop = useCallback(
    (acceptedFiles: FileWithPath[]) => {
      handleImageUpload(acceptedFiles);
    },
    [handleImageUpload]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { "image/*": [] },
    multiple: false,
    noClick: true,
    noKeyboard: true,
    disabled: isSpacePressed,
  });

  const handleStageClick = (e: Konva.KonvaEventObject<MouseEvent>) => {
    if (!image || isSpacePressed) return;

    const clickedOnCircle = e.target.className === "Circle";
    if (clickedOnCircle) {
      const circleId = e.target.attrs.id;
      if (circleId) {
        setColorSpots((prevSpots) =>
          prevSpots.filter((spot) => spot.id !== parseInt(circleId))
        );
        return;
      }
    }

    const stage = e.target.getStage();
    const pointerPosition = stage?.getPointerPosition();
    if (pointerPosition) {
      const x = (pointerPosition.x - stageTransform.x) / stageTransform.scale;
      const y = (pointerPosition.y - stageTransform.y) / stageTransform.scale;

      // Ensure click is within image boundaries (pixels are 0-indexed)
      if (x < 0 || x >= imageSize.width || y < 0 || y >= imageSize.height) {
        return;
      }

      const canvas = document.createElement("canvas");
      canvas.width = imageSize.width;
      canvas.height = imageSize.height;
      const ctx = canvas.getContext("2d");
      if (ctx) {
        ctx.drawImage(image, 0, 0, imageSize.width, imageSize.height);
        const imageData = ctx.getImageData(Math.round(x), Math.round(y), 1, 1);
        const r = imageData.data[0];
        const g = imageData.data[1];
        const b = imageData.data[2];
        const color = `rgb(${r},${g},${b})`;
        setColorSpots((prevSpots) => [...prevSpots, { id: nextId, x, y, color }]);
        setNextId(nextId + 1);
      }
    }
  };

  const handleExtractDominantColors = () => {
    if (!image) return;
    const { spots, nextId: newNextId } = extractDominantColors(
      image,
      imageSize,
      nextId
    );
    setColorSpots(spots);
    setNextId(newNextId);
  };

  const copyToClipboard = (color: string) => {
    const hex = rgbToHex(color);
    navigator.clipboard.writeText(hex).then(() => {
      setToast({ message: `Copied ${hex} to clipboard!`, type: 'success' });
    }).catch(() => {
      setToast({ message: 'Failed to copy color', type: 'error' });
    });
  };

  const stageWidth =
    windowSize.width > 768 ? windowSize.width * 0.65 : windowSize.width * 0.95;
  const stageHeight = windowSize.height * 0.8;

  return (
    <div className="App" {...getRootProps()}>
      {/* Floating background orbs */}
      <div className="floating-orbs">
        <div className="orb orb-1"></div>
        <div className="orb orb-2"></div>
        <div className="orb orb-3"></div>
      </div>
      <input {...getInputProps()} />
      {!image ? (
        isDragActive ? (
          <Paper
            elevation={3}
            sx={{
              p: 5,
              backgroundColor: "rgba(0, 0, 0, 0.04)",
              border: "2px dashed #2196f3",
              borderRadius: 2,
              textAlign: "center",
              cursor: "pointer",
              transition: "background-color 0.3s",
              "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.08)" },
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 2,
              }}
            >
              <CloudUploadIcon sx={{ fontSize: 48, color: "#2196f3" }} />
              <Typography variant="h6" color="primary">
                Drop the image here ...
              </Typography>
            </Box>
          </Paper>
        ) : (
          <WelcomeScreen onImageUpload={handleImageUpload} onImageUrlLoad={handleImageUrlLoad} isImageLoaded={false} />
        )
      ) : (
        <div className="app-container">
          <div className="color-palette-sidebar">
            <h3>Color Palette</h3>
            <div className="color-list">
              {colorSpots.map((spot, index) => (
                <div key={spot.id} className="color-item-wrapper">
                  <div className="color-item">
                    <div className="color-number">{index + 1}</div>
                    <div
                      className="color-swatch"
                      style={{ backgroundColor: spot.color }}
                      onClick={() => setHarmonyColorId(harmonyColorId === spot.id ? null : spot.id)}
                      title="Click for color harmonies"
                    />
                    <div
                      className="color-value"
                      onClick={() => copyToClipboard(spot.color)}
                      title="Click to copy"
                    >
                      {rgbToHex(spot.color)}
                    </div>
                    <IconButton
                      size="small"
                      onClick={() => copyToClipboard(spot.color)}
                      className="copy-button"
                      title="Copy color"
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() =>
                        setColorSpots((prev) => prev.filter((s) => s.id !== spot.id))
                      }
                      className="delete-button"
                      title="Delete color"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </div>
                  {harmonyColorId === spot.id && (
                    <ColorHarmonyPanel
                      baseColor={spot.color}
                      onAddColor={(color) => {
                        setColorSpots((prev) => [...prev, { id: nextId, x: spot.x, y: spot.y, color }]);
                        setNextId(nextId + 1);
                        setToast({ message: `Added ${rgbToHex(color)} to palette`, type: 'success' });
                      }}
                      onClose={() => setHarmonyColorId(null)}
                    />
                  )}
                </div>
              ))}
              {colorSpots.length === 0 && (
                <p className="empty-message">Click on the image to select colors</p>
              )}
            </div>
          </div>

          <div className="canvas-container">
            <Stage
              width={stageWidth}
              height={stageHeight}
              onWheel={handleWheel}
              onClick={handleStageClick}
              onTap={handleStageClick}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
              onTouchStart={handleMouseDown}
              onTouchMove={handleMouseMove}
              onTouchEnd={handleMouseUp}
              scaleX={stageTransform.scale}
              scaleY={stageTransform.scale}
              x={stageTransform.x}
              y={stageTransform.y}
              ref={stageRef}
              draggable={false}
              style={{ cursor: getCursor() }}
            >
              <Layer>
                <Image
                  image={image}
                  width={imageSize.width}
                  height={imageSize.height}
                  draggable={false}
                />
                {colorSpots.map((spot, i) => (
                  <React.Fragment key={spot.id}>
                    <Circle
                      id={spot.id.toString()}
                      x={spot.x}
                      y={spot.y}
                      radius={25 / stageTransform.scale}
                      fill={spot.color}
                      stroke="white"
                      strokeWidth={3 / stageTransform.scale}
                      shadowColor="black"
                      shadowBlur={5 / stageTransform.scale}
                      shadowOpacity={0.3}
                      cursor="pointer"
                    />
                    <Text
                      x={spot.x - 25 / stageTransform.scale}
                      y={spot.y - 8 / stageTransform.scale}
                      width={50 / stageTransform.scale}
                      text={(i + 1).toString()}
                      fontSize={16 / stageTransform.scale}
                      fontStyle="bold"
                      fill="white"
                      align="center"
                      listening={false}
                    />
                  </React.Fragment>
                ))}
              </Layer>
            </Stage>
          </div>

          <div className="controls-panel">
            <h4>Controls</h4>
            <div className="button-container">
              <Button
                variant="contained"
                onClick={() => setIsExportModalOpen(true)}
                disabled={colorSpots.length === 0}
              >
                Export Options
              </Button>
              <Button variant="contained" onClick={handleExtractDominantColors}>
                Extract Colors
              </Button>
              <Button variant="contained" onClick={() => setColorSpots([])}>
                Delete All Selections
              </Button>
              <Button
                variant="contained"
                onClick={() => {
                  setImage(null);
                  setColorSpots([]);
                }}
              >
                Upload New Image
              </Button>
            </div>
          </div>

          <ExportModal
            open={isExportModalOpen}
            onClose={() => setIsExportModalOpen(false)}
            onExportWithBackground={() => {
              exportWithBackground(image, colorSpots);
              setIsExportModalOpen(false);
              setToast({ message: 'Exported image with background!', type: 'success' });
            }}
            onExportPaletteOnly={() => {
              exportWhiteBackground(image, colorSpots);
              setIsExportModalOpen(false);
              setToast({ message: 'Exported palette on white background!', type: 'success' });
            }}
            onExportColorPalette={() => {
              exportColorSwatches(colorSpots);
              setIsExportModalOpen(false);
              setToast({ message: 'Exported color swatches!', type: 'success' });
            }}
            onExportTransparent={() => {
              exportTransparent(image, colorSpots);
              setIsExportModalOpen(false);
              setToast({ message: 'Exported with transparent background!', type: 'success' });
            }}
          />
        </div>
      )}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
}

export default App;
